/**
 * Faixa do paciente sempre à vista (pedido de 23/10/2026): quem decide o que
 * sai é a LARGURA que sobra pra ficha — não a da tela. No notebook a tela não
 * muda, mas abrir o ditado à esquerda e a agenda à direita deixa o miolo com
 * ~850px. Nome, idade e convênio nunca saem; o resto sai nesta ordem: Sede →
 * Agenda → laterais/tela cheia/sino/Sair (tudo pro "⋯") → abas da linha
 * recolhida (voltam pra linha própria) → avatar/voltar/lixeira → nomes do
 * meio (iniciais). Os atalhos Gravar/Extrair/Câmera moram no menu da
 * esquerda; o Ditar fica na barra, ao lado dos microfones.
 *
 * Funções puras — os limiares são estimativas da largura de cada peça, em px.
 */

/** Quanto da direita da barra do topo já foi pro "⋯".
 *  0 = nada · 1 = unidade · 2 = + Agenda · 3 = + laterais, tela cheia, sino, Sair. */
export type TopbarCollapse = 0 | 1 | 2 | 3;

/** Esquerda da barra (botão do menu) e direita em cada nível de recolhimento. */
const BAR_LEFT = 60;
const BAR_RIGHT: Record<TopbarCollapse, number> = { 0: 430, 1: 330, 2: 230, 3: 50 };

/** Peças da faixa do paciente, em px. */
const EXPANDED_NEED = 820; // cabeçalho expandido com o nome inteiro numa linha
const IDENTITY_MIN = 560; // voltar + avatar + nome inteiro + idade + convênio + lixeira
const TOGGLE = 40; // botão recolher/expandir
const TAB_ICON = 34; // aba só ícone (32px + respiro)

/**
 * Quanto a faixa do paciente precisa da barra do topo pra mostrar o nome
 * inteiro no modo em que está. A barra recolhe a direita até sobrar isso —
 * assim Sede/Agenda saem ANTES de o nome abreviar.
 */
export function fichaSlotNeed(opts: { compact: boolean }): number {
  return opts.compact ? IDENTITY_MIN + TOGGLE : EXPANDED_NEED;
}

/** Botão dos microfones na ficha (só os pontos), que nunca vai pro "⋯". */
export const MICS_TOPO = 90;

/** Botão "Ditar" ao lado dos microfones (29/10/2026), que também nunca vai pro "⋯". */
export const DITAR_TOPO = 85;

/**
 * Nível de recolhimento da direita da barra do topo: o menor que deixa
 * `need` px pra faixa do paciente. `need` 0 = a página não pôs nada na barra
 * (fora da ficha) → nada recolhe. Largura 0 = ainda não medida → idem.
 * `extra`: o que fica à direita em todo nível (os microfones).
 */
export function topbarCollapseLevel(width: number, need: number, extra = 0): TopbarCollapse {
  if (!need || width <= 0) return 0;
  for (const level of [0, 1, 2] as const) {
    if (width - BAR_LEFT - BAR_RIGHT[level] - extra >= need) return level;
  }
  return 3;
}

/**
 * As abas (só ícone) cabem na linha RECOLHIDA do cabeçalho, ao lado do
 * paciente? Se não, voltam pra linha própria — o paciente tem prioridade.
 * Largura 0 = ainda não medida → cabem (monta como antes).
 */
export function fichaTabsFitInRow(width: number, tabCount: number): boolean {
  return width <= 0 || width >= IDENTITY_MIN + tabCount * TAB_ICON + 8 + TOGGLE;
}
